type FigmaExportColor = {
    a: number;
    b: number;
    g: number;
    r: number;
};

type FigmaExportNodeStyle = {
    backgroundColor?: FigmaExportColor;
    borderColor?: FigmaExportColor;
    borderRadii?: FigmaExportBorderRadii;
    borders?: Partial<Record<FigmaExportBorderSide, FigmaExportBorder>>;
    borderRadius?: number;
    borderWidth?: number;
    color?: FigmaExportColor;
    fontFamily?: string;
    fontSize?: number;
    fontWeight?: number;
    lineHeight?: number;
    opacity?: number;
    overflowHidden?: boolean;
    shadows?: FigmaExportShadow[];
    textAlign?: string;
    textTransform?: string;
};

type FigmaExportBorderSide = 'bottom' | 'left' | 'right' | 'top';

type FigmaExportBorder = {
    color: FigmaExportColor;
    width: number;
};

type FigmaExportBorderRadii = {
    bottomLeft: number;
    bottomRight: number;
    topLeft: number;
    topRight: number;
};

type FigmaExportShadow = {
    blur: number;
    color: FigmaExportColor;
    inset: boolean;
    spread: number;
    x: number;
    y: number;
};

type FigmaExportNode = {
    children?: FigmaExportNode[];
    height: number;
    name: string;
    paintOrder?: 'foreground';
    points?: FigmaExportPoint[];
    sourceMode?: 'image' | 'mask';
    style: FigmaExportNodeStyle;
    sourceUrl?: string;
    svg?: string;
    text?: string;
    textAutoResize?: 'fixed' | 'widthAndHeight';
    type: 'frame' | 'polygon' | 'svg' | 'text';
    width: number;
    x: number;
    y: number;
};

type FigmaExportPoint = {
    x: number;
    y: number;
};

type FigmaExportPayload = {
    generator: 'uikit-demo';
    root: FigmaExportNode;
    version: 1;
};

type ElementNodeOptions = {
    useEffectiveBackgroundColor?: boolean;
};

const transparentColor: FigmaExportColor = { r: 0, g: 0, b: 0, a: 0 };

const iconSvgCache = new Map<string, Promise<string | null>>();

const getNumberValue = (value: string) => Number.parseFloat(value) || 0;

const normalizeName = (value: string) => value.replace(/\s+/g, ' ').trim();

const getOpacity = (style: CSSStyleDeclaration) => {
    const opacity = Number.parseFloat(style.opacity);

    return Number.isFinite(opacity) ? opacity : 1;
};

const getNodeName = (element: HTMLElement) => {
    const label = element.getAttribute('aria-label') || element.getAttribute('title') || element.textContent;
    const normalizedLabel = label ? normalizeName(label).slice(0, 40) : '';

    if (normalizedLabel) {
        return `${element.tagName.toLowerCase()} ${normalizedLabel}`;
    }

    return element.tagName.toLowerCase();
};

const parseKnownColor = (value: string): FigmaExportColor | undefined => {
    if (!value || value === 'transparent') {
        return;
    }

    const hexMatch = value.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);

    if (hexMatch) {
        const hex = hexMatch[1];
        const normalizedHex =
            hex.length === 3
                ? hex
                      .split('')
                      .map(character => `${character}${character}`)
                      .join('')
                : hex;

        return {
            r: Number.parseInt(normalizedHex.slice(0, 2), 16) / 255,
            g: Number.parseInt(normalizedHex.slice(2, 4), 16) / 255,
            b: Number.parseInt(normalizedHex.slice(4, 6), 16) / 255,
            a: 1,
        };
    }

    const cssColorMatch = value.match(/^color\((?:srgb|display-p3)\s+([^)]*)\)$/);

    if (cssColorMatch) {
        const [r, g, b, a = '1'] = cssColorMatch[1].replace('/', ' ').split(/\s+/).filter(Boolean);
        const alpha = Number.parseFloat(a);

        return {
            r: Number.parseFloat(r),
            g: Number.parseFloat(g),
            b: Number.parseFloat(b),
            a: Number.isFinite(alpha) ? alpha : 1,
        };
    }

    const match = value.match(/^rgba?\(([^)]+)\)$/);

    if (!match) {
        return;
    }

    const [r, g, b, a = '1'] = match[1]
        .replace('/', ' ')
        .split(/[,\s]+/)
        .filter(Boolean);
    const alpha = Number.parseFloat(a);

    if (alpha === 0) {
        return;
    }

    return {
        r: Number.parseFloat(r) / 255,
        g: Number.parseFloat(g) / 255,
        b: Number.parseFloat(b) / 255,
        a: Number.isFinite(alpha) ? alpha : 1,
    };
};

const getColorMixOperand = (value: string, contextElement: HTMLElement) => {
    const trimmedValue = value.trim();
    const variableMatch = trimmedValue.match(/^var\((--[^),]+)(?:,\s*([^)]+))?\)$/);

    if (!variableMatch) {
        return trimmedValue;
    }

    return (
        window.getComputedStyle(contextElement).getPropertyValue(variableMatch[1]).trim() ||
        variableMatch[2]?.trim() ||
        trimmedValue
    );
};

const splitTopLevelCssList = (value: string) => {
    const parts: string[] = [];
    let currentPart = '';
    let depth = 0;

    for (const character of value) {
        if (character === '(') {
            depth += 1;
        } else if (character === ')') {
            depth -= 1;
        } else if (character === ',' && depth === 0) {
            parts.push(currentPart.trim());
            currentPart = '';
            continue;
        }

        currentPart += character;
    }

    if (currentPart.trim()) {
        parts.push(currentPart.trim());
    }

    return parts;
};

const mixColors = (firstColor: FigmaExportColor, secondColor: FigmaExportColor, firstWeight: number) => {
    const secondWeight = 1 - firstWeight;

    return {
        r: firstColor.r * firstWeight + secondColor.r * secondWeight,
        g: firstColor.g * firstWeight + secondColor.g * secondWeight,
        b: firstColor.b * firstWeight + secondColor.b * secondWeight,
        a: firstColor.a * firstWeight + secondColor.a * secondWeight,
    };
};

const parseColorMixPart = (value: string) => {
    const trimmedValue = value.trim();
    const weightMatch = trimmedValue.match(/^(.*?)\s+([\d.]+)%$/);

    if (!weightMatch) {
        return {
            color: trimmedValue,
            weight: undefined,
        };
    }

    return {
        color: weightMatch[1].trim(),
        weight: Number.parseFloat(weightMatch[2]) / 100,
    };
};

const parseColorMixOperand = (value: string, contextElement: HTMLElement): FigmaExportColor | undefined => {
    const operand = getColorMixOperand(value, contextElement);

    if (operand === 'transparent') {
        return transparentColor;
    }

    const knownColor = parseKnownColor(operand);

    if (knownColor) {
        return knownColor;
    }

    const resolvedColor = resolveCssColor(operand, contextElement);

    return resolvedColor ? parseKnownColor(resolvedColor) : undefined;
};

const parseColorMix = (value: string, contextElement: HTMLElement): FigmaExportColor | undefined => {
    const trimmedValue = value.trim();

    if (!trimmedValue.startsWith('color-mix(') || !trimmedValue.endsWith(')')) {
        return;
    }

    const content = trimmedValue.slice('color-mix('.length, -1).trim();
    const prefix = 'in srgb,';

    if (!content.startsWith(prefix)) {
        return;
    }

    const [firstPart, secondPart] = splitTopLevelCssList(content.slice(prefix.length));

    if (!firstPart || !secondPart) {
        return;
    }

    const first = parseColorMixPart(firstPart);
    const second = parseColorMixPart(secondPart);
    const firstColor = parseColorMixOperand(first.color, contextElement);
    const secondColor = parseColorMixOperand(second.color, contextElement);
    const firstWeight = first.weight ?? (second.weight === undefined ? 0.5 : 1 - second.weight);

    if (!firstColor || !secondColor || !Number.isFinite(firstWeight)) {
        return;
    }

    return mixColors(firstColor, secondColor, firstWeight);
};

const resolveCssColor = (value: string, contextElement: HTMLElement) => {
    const element = document.createElement('span');
    const parentElement = contextElement.isConnected && contextElement.append ? contextElement : document.body;

    element.style.color = value;

    if (!element.style.color) {
        return;
    }

    element.style.position = 'absolute';
    element.style.pointerEvents = 'none';
    element.style.visibility = 'hidden';
    parentElement.appendChild(element);

    const resolvedColor = window.getComputedStyle(element).color;
    element.remove();

    return resolvedColor;
};

const resolveCssLength = (value: string, property: 'font-size' | 'line-height', contextElement: HTMLElement) => {
    const element = document.createElement('span');
    const parentElement = contextElement.isConnected && contextElement.append ? contextElement : document.body;

    element.style.setProperty(property, value);

    if (!element.style.getPropertyValue(property)) {
        return;
    }

    element.style.position = 'absolute';
    element.style.pointerEvents = 'none';
    element.style.visibility = 'hidden';
    parentElement.appendChild(element);

    const resolvedValue = window.getComputedStyle(element).getPropertyValue(property);
    element.remove();

    return getNumberValue(resolvedValue);
};

const parseColor = (
    value: string,
    contextElement: HTMLElement = document.documentElement
): FigmaExportColor | undefined => {
    const knownColor = parseKnownColor(value);

    if (knownColor) {
        return knownColor;
    }

    const mixedColor = parseColorMix(value, contextElement);

    if (mixedColor) {
        return mixedColor;
    }

    const resolvedColor = resolveCssColor(value, contextElement);

    return resolvedColor && resolvedColor !== value ? parseKnownColor(resolvedColor) : undefined;
};

const colorToCss = (color: FigmaExportColor) => {
    const r = Math.round(color.r * 255);
    const g = Math.round(color.g * 255);
    const b = Math.round(color.b * 255);

    if (color.a >= 1) {
        return `#${[r, g, b].map(value => value.toString(16).padStart(2, '0')).join('')}`;
    }

    return `rgba(${r}, ${g}, ${b}, ${color.a})`;
};

const hasVisibleBoxStyle = (style: CSSStyleDeclaration) =>
    Boolean(parseColor(style.backgroundColor) || getBorders(style) || parseBoxShadow(style.boxShadow));

const getRelativeRect = (rect: DOMRect, rootRect: DOMRect) => ({
    x: rect.left - rootRect.left,
    y: rect.top - rootRect.top,
    width: rect.width,
    height: rect.height,
});

const getCenteredIconRect = (element: HTMLElement, iconStyle: CSSStyleDeclaration, rootRect: DOMRect) => {
    const rect = element.getBoundingClientRect();
    const fontSize = getNumberValue(iconStyle.fontSize);
    const iconWidth = getNumberValue(iconStyle.width) || fontSize || rect.width;
    const iconHeight = getNumberValue(iconStyle.height) || fontSize || rect.height;
    const width = fontSize && iconWidth > fontSize * 1.5 ? fontSize : Math.min(rect.width, iconWidth);
    const height = fontSize && iconHeight > fontSize * 1.5 ? fontSize : Math.min(rect.height, iconHeight);

    return {
        x: rect.left - rootRect.left + (rect.width - width) / 2,
        y: rect.top - rootRect.top + (rect.height - height) / 2,
        width,
        height,
    };
};

const getTranslateOffset = (transform: string) => {
    if (!transform || transform === 'none') {
        return { x: 0, y: 0 };
    }

    try {
        const matrix = new DOMMatrixReadOnly(transform);

        return {
            x: matrix.m41,
            y: matrix.m42,
        };
    } catch {
        return { x: 0, y: 0 };
    }
};

const getPseudoElementRect = (element: HTMLElement, style: CSSStyleDeclaration, rootRect: DOMRect) => {
    const rect = element.getBoundingClientRect();
    const left = style.left === 'auto' ? undefined : getNumberValue(style.left);
    const right = style.right === 'auto' ? undefined : getNumberValue(style.right);
    const top = style.top === 'auto' ? undefined : getNumberValue(style.top);
    const bottom = style.bottom === 'auto' ? undefined : getNumberValue(style.bottom);
    const marginLeft = getNumberValue(style.marginLeft);
    const marginRight = getNumberValue(style.marginRight);
    const marginTop = getNumberValue(style.marginTop);
    const marginBottom = getNumberValue(style.marginBottom);
    let width = getNumberValue(style.width);
    let height = getNumberValue(style.height);

    if (width <= 0 && left !== undefined && right !== undefined) {
        width = Math.max(0, rect.width - left - right - marginLeft - marginRight);
    }

    if (height <= 0 && top !== undefined && bottom !== undefined) {
        height = Math.max(0, rect.height - top - bottom - marginTop - marginBottom);
    }

    if (width <= 0 || height <= 0) {
        const probe = document.createElement('span');

        probe.style.position = style.position || 'absolute';
        probe.style.pointerEvents = 'none';
        probe.style.visibility = 'hidden';
        probe.style.display = style.display;
        probe.style.width = style.width;
        probe.style.height = style.height;
        probe.style.boxSizing = style.boxSizing;
        probe.style.margin = style.margin;
        probe.style.transform = style.transform;
        probe.style.transformOrigin = style.transformOrigin;

        if (style.left !== 'auto') {
            probe.style.left = style.left;
        }

        if (style.right !== 'auto') {
            probe.style.right = style.right;
        }

        if (style.top !== 'auto') {
            probe.style.top = style.top;
        }

        if (style.bottom !== 'auto') {
            probe.style.bottom = style.bottom;
        }

        element.appendChild(probe);

        const probeRect = probe.getBoundingClientRect();
        probe.remove();

        return getRelativeRect(probeRect, rootRect);
    }

    const translate = getTranslateOffset(style.transform);
    const x =
        left !== undefined
            ? left + marginLeft
            : right !== undefined
              ? rect.width - right - width - marginRight
              : marginLeft;
    const y =
        top !== undefined
            ? top + marginTop
            : bottom !== undefined
              ? rect.height - bottom - height - marginBottom
              : marginTop;

    return {
        x: rect.left - rootRect.left + x + translate.x,
        y: rect.top - rootRect.top + y + translate.y,
        width,
        height,
    };
};

const getPseudoElementBorderBoxRect = ({
    element,
    rootRect,
    style,
}: {
    element: HTMLElement;
    rootRect: DOMRect;
    style: CSSStyleDeclaration;
}) => {
    const probe = document.createElement('span');

    probe.style.position = style.position || 'absolute';
    probe.style.pointerEvents = 'none';
    probe.style.visibility = 'hidden';
    probe.style.width = style.width;
    probe.style.height = style.height;
    probe.style.boxSizing = style.boxSizing;
    probe.style.borderStyle = style.borderStyle;
    probe.style.borderWidth = style.borderWidth;
    probe.style.margin = style.margin;
    probe.style.transform = style.transform;
    probe.style.transformOrigin = style.transformOrigin;

    if (style.left !== 'auto') {
        probe.style.left = style.left;
    }

    if (style.right !== 'auto') {
        probe.style.right = style.right;
    }

    if (style.top !== 'auto') {
        probe.style.top = style.top;
    }

    if (style.bottom !== 'auto') {
        probe.style.bottom = style.bottom;
    }

    element.appendChild(probe);

    const rect = probe.getBoundingClientRect();
    probe.remove();

    return {
        x: rect.left - rootRect.left,
        y: rect.top - rootRect.top,
        width: rect.width,
        height: rect.height,
    };
};

const getFontFamily = (fontFamily: string) =>
    fontFamily
        .split(',')
        .map(family => family.trim().replace(/^['"]|['"]$/g, ''))
        .filter(Boolean)[0] || 'Inter';

const getLineHeight = (style: CSSStyleDeclaration, contextElement: HTMLElement) => {
    if (style.lineHeight === 'normal') {
        const fontSize =
            resolveCssLength(style.fontSize, 'font-size', contextElement) || getNumberValue(style.fontSize);

        return fontSize * 1.2;
    }

    return resolveCssLength(style.lineHeight, 'line-height', contextElement) || getNumberValue(style.lineHeight);
};

const getEffectiveOpacity = (
    element: HTMLElement,
    rootElement: HTMLElement,
    leafStyle?: CSSStyleDeclaration,
    includeRootElement = false
) => {
    let opacity = leafStyle ? getOpacity(leafStyle) : 1;
    let currentElement: HTMLElement | null = element;

    while (currentElement) {
        if (currentElement === rootElement) {
            if (includeRootElement) {
                opacity *= getOpacity(window.getComputedStyle(currentElement));
            }
            break;
        }

        opacity *= getOpacity(window.getComputedStyle(currentElement));
        currentElement = currentElement.parentElement;
    }

    return Math.max(0, Math.min(1, opacity));
};

const getPositiveZIndex = (element: HTMLElement) => {
    const style = window.getComputedStyle(element);
    const zIndex = Number.parseInt(style.zIndex, 10);

    return style.position !== 'static' && Number.isFinite(zIndex) && zIndex > 0 ? zIndex : 0;
};

const shouldIgnoreExportElement = (element: HTMLElement) =>
    Array.from(element.classList).some(className => className.startsWith('scrollbar-track-'));

const isSingleLineText = (range: Range, style: CSSStyleDeclaration) => {
    if (style.whiteSpace === 'nowrap') {
        return true;
    }

    return range.getClientRects().length <= 1;
};

const isFlexDisplay = (display: string) => display === 'flex' || display === 'inline-flex';

const hasFlexAncestor = (element: HTMLElement, stopElement?: HTMLElement) => {
    let currentElement = element.parentElement;

    while (currentElement) {
        if (currentElement === stopElement) {
            return false;
        }

        if (isFlexDisplay(window.getComputedStyle(currentElement).display)) {
            return true;
        }

        currentElement = currentElement.parentElement;
    }

    return false;
};

const splitCssList = (value: string) => {
    const parts: string[] = [];
    let currentPart = '';
    let depth = 0;

    for (const character of value) {
        if (character === '(') {
            depth += 1;
        } else if (character === ')') {
            depth -= 1;
        } else if (character === ',' && depth === 0) {
            parts.push(currentPart.trim());
            currentPart = '';
            continue;
        }

        currentPart += character;
    }

    if (currentPart.trim()) {
        parts.push(currentPart.trim());
    }

    return parts;
};

const splitCssTokens = (value: string) => value.match(/(?:[^\s(]+?\([^)]*\)|[^\s]+)/g) ?? [];

const getBorder = (style: CSSStyleDeclaration, side: FigmaExportBorderSide): FigmaExportBorder | undefined => {
    const sideName = `${side[0].toUpperCase()}${side.slice(1)}`;
    const borderStyle = style.getPropertyValue(`border-${side}-style`);
    const borderWidth = getNumberValue(style.getPropertyValue(`border-${side}-width`));
    const borderColor = parseColor(style.getPropertyValue(`border-${side}-color`));

    if (borderWidth <= 0 || borderStyle === 'none' || !borderColor) {
        return;
    }

    return {
        color: borderColor,
        width: getNumberValue(style.getPropertyValue(`border${sideName}Width`)) || borderWidth,
    };
};

const getBorders = (style: CSSStyleDeclaration) => {
    const borders: Partial<Record<FigmaExportBorderSide, FigmaExportBorder>> = {};

    (['top', 'right', 'bottom', 'left'] as const).forEach(side => {
        const border = getBorder(style, side);

        if (border) {
            borders[side] = border;
        }
    });

    return Object.keys(borders).length > 0 ? borders : undefined;
};

const getBorderRadii = (style: CSSStyleDeclaration): FigmaExportBorderRadii | undefined => {
    const radii = {
        topLeft: getNumberValue(style.borderTopLeftRadius),
        topRight: getNumberValue(style.borderTopRightRadius),
        bottomRight: getNumberValue(style.borderBottomRightRadius),
        bottomLeft: getNumberValue(style.borderBottomLeftRadius),
    };

    return Object.values(radii).some(radius => radius > 0) ? radii : undefined;
};

const getUniformBorderRadius = (borderRadii?: FigmaExportBorderRadii) => {
    if (!borderRadii) {
        return;
    }

    const values = Object.values(borderRadii);
    const firstValue = values[0];

    return values.every(value => value === firstValue) ? firstValue : undefined;
};

const parseBoxShadow = (value: string): FigmaExportShadow[] | undefined => {
    if (!value || value === 'none') {
        return;
    }

    const shadows = splitCssList(value)
        .map(shadow => {
            const tokens = splitCssTokens(shadow);
            const colorToken = tokens.find(token => parseColor(token));
            const color = colorToken ? parseColor(colorToken) : undefined;

            if (!color) {
                return null;
            }

            const lengths = tokens
                .filter(token => token !== colorToken && token !== 'inset')
                .map(getNumberValue)
                .filter(value => Number.isFinite(value));

            return {
                color,
                inset: tokens.includes('inset'),
                x: lengths[0] ?? 0,
                y: lengths[1] ?? 0,
                blur: lengths[2] ?? 0,
                spread: lengths[3] ?? 0,
            };
        })
        .filter((shadow): shadow is FigmaExportShadow => shadow !== null);

    return shadows.length > 0 ? shadows : undefined;
};

const getEffectiveBackgroundColor = (element: HTMLElement) => {
    let currentElement: HTMLElement | null = element;

    while (currentElement) {
        const backgroundColor = parseColor(window.getComputedStyle(currentElement).backgroundColor);

        if (backgroundColor) {
            return backgroundColor;
        }

        currentElement = currentElement.parentElement;
    }
};

const getElementStyle = (
    style: CSSStyleDeclaration,
    fallbackBackgroundColor: FigmaExportColor | undefined,
    opacity: number
): FigmaExportNodeStyle => {
    const backgroundColor = parseColor(style.backgroundColor) ?? fallbackBackgroundColor;
    const borders = getBorders(style);
    const borderRadii = getBorderRadii(style);
    const borderColor = borders?.top?.color;
    const borderWidth = borders?.top?.width;

    return {
        backgroundColor,
        borderRadii,
        borders,
        borderColor,
        borderRadius: getUniformBorderRadius(borderRadii),
        borderWidth,
        opacity,
        overflowHidden: ['clip', 'hidden'].includes(style.overflowX) || ['clip', 'hidden'].includes(style.overflowY),
        shadows: parseBoxShadow(style.boxShadow),
    };
};

const extractCssUrl = (value: string) => {
    const match = value.match(/url\((['"]?)(.*?)\1\)/);

    if (!match?.[2]) {
        return;
    }

    return match[2];
};

const getIconUrl = (style: CSSStyleDeclaration) =>
    extractCssUrl(style.getPropertyValue('--i')) ||
    extractCssUrl(style.getPropertyValue('mask-image')) ||
    extractCssUrl(style.getPropertyValue('-webkit-mask-image'));

const getBackgroundImageUrl = (style: CSSStyleDeclaration) => extractCssUrl(style.backgroundImage);

const normalizeSvgForFigma = (svg: string, color: FigmaExportColor) => {
    const cssColor = colorToCss(color);
    const coloredSvg = svg
        .replace(/currentcolor/gi, cssColor)
        .replace(/((?:fill|stroke)\s*:\s*)(?!none\b)[^;"']+/gi, `$1${cssColor}`)
        .replace(
            /\s(fill|stroke)=(['"])(?!none\b)(.*?)\2/gi,
            (_match, attribute, quote) => ` ${attribute}=${quote}${cssColor}${quote}`
        );

    if (/\sfill=(['"])(?!none\b)(.*?)\1/i.test(coloredSvg)) {
        return coloredSvg;
    }

    return coloredSvg.replace('<svg', `<svg fill="${cssColor}"`);
};

const fetchIconSvg = (url: string) => {
    if (!iconSvgCache.has(url)) {
        iconSvgCache.set(
            url,
            fetch(url)
                .then(response => (response.ok ? response.text() : null))
                .catch(() => null)
        );
    }

    return iconSvgCache.get(url) ?? Promise.resolve(null);
};

const getIconColor = (element: HTMLElement, style: CSSStyleDeclaration) =>
    parseColor(style.backgroundColor, element) ??
    parseColor(style.color, element) ??
    parseColor(window.getComputedStyle(element).color, element) ??
    transparentColor;

const getBorderTrianglePoints = (
    side: FigmaExportBorderSide,
    widths: Record<FigmaExportBorderSide, number>,
    width: number,
    height: number
): FigmaExportPoint[] => {
    const contentX = widths.left;
    const contentY = widths.top;

    if (side === 'right') {
        return [
            { x: contentX, y: contentY },
            { x: width, y: 0 },
            { x: width, y: height },
        ];
    }

    if (side === 'left') {
        return [
            { x: contentX, y: contentY },
            { x: 0, y: 0 },
            { x: 0, y: height },
        ];
    }

    if (side === 'top') {
        return [
            { x: contentX, y: contentY },
            { x: 0, y: 0 },
            { x: width, y: 0 },
        ];
    }

    return [
        { x: contentX, y: contentY },
        { x: 0, y: height },
        { x: width, y: height },
    ];
};

const isBorderTriangleSize = (style: CSSStyleDeclaration, width: number, height: number) => {
    const reportedWidth = getNumberValue(style.width);
    const reportedHeight = getNumberValue(style.height);
    const tolerance = 1;

    return (
        reportedWidth <= tolerance ||
        reportedHeight <= tolerance ||
        (reportedWidth <= width + tolerance && reportedHeight <= height + tolerance)
    );
};

const getPseudoElementBorderTriangleSvgNode = (
    element: HTMLElement,
    pseudoElement: '::after' | '::before',
    rootRect: DOMRect,
    rootElement: HTMLElement
): FigmaExportNode | null => {
    const style = window.getComputedStyle(element, pseudoElement);

    if (style.content === 'none' || getIconUrl(style)) {
        return null;
    }

    const widths = {
        top: getNumberValue(style.borderTopWidth),
        right: getNumberValue(style.borderRightWidth),
        bottom: getNumberValue(style.borderBottomWidth),
        left: getNumberValue(style.borderLeftWidth),
    };
    const visibleBorders = (['top', 'right', 'bottom', 'left'] as const)
        .map(side => ({
            side,
            color: parseColor(style.getPropertyValue(`border-${side}-color`), element),
            style: style.getPropertyValue(`border-${side}-style`),
            width: widths[side],
        }))
        .filter(border => border.color && border.color.a > 0 && border.style !== 'none' && border.width > 0);

    if (visibleBorders.length !== 1) {
        return null;
    }

    const border = visibleBorders[0];
    const width = widths.left + widths.right;
    const height = widths.top + widths.bottom;

    if (width <= 0 || height <= 0) {
        return null;
    }

    if (!isBorderTriangleSize(style, width, height)) {
        return null;
    }

    const rect = getPseudoElementBorderBoxRect({ element, rootRect, style });
    const points = getBorderTrianglePoints(border.side, widths, width, height);

    return {
        type: 'polygon',
        name: `${pseudoElement.replace('::', '')} border triangle`,
        points,
        x: rect.x,
        y: rect.y,
        width: rect.width,
        height: rect.height,
        style: {
            backgroundColor: border.color,
            opacity: getEffectiveOpacity(element, rootElement, style),
        },
        paintOrder: 'foreground',
    };
};

const getPseudoElementSvgNode = async (
    element: HTMLElement,
    pseudoElement: '::after' | '::before',
    rootRect: DOMRect,
    rootElement: HTMLElement
): Promise<FigmaExportNode | null> => {
    const style = window.getComputedStyle(element, pseudoElement);
    const iconUrl = getIconUrl(style);

    if (!iconUrl || style.content === 'none') {
        return null;
    }

    const svg = await fetchIconSvg(iconUrl);
    const rect = element.getBoundingClientRect();

    if (!svg || rect.width <= 0 || rect.height <= 0) {
        return null;
    }

    const color = getIconColor(element, style);
    const relativeRect = getCenteredIconRect(element, style, rootRect);

    return {
        type: 'svg',
        name: `icon ${iconUrl.split('/').pop()?.replace('.svg', '') ?? 'uikit-icon'}`,
        sourceUrl: iconUrl,
        svg: normalizeSvgForFigma(svg, color),
        x: relativeRect.x,
        y: relativeRect.y,
        width: relativeRect.width,
        height: relativeRect.height,
        style: {
            color,
            opacity: getEffectiveOpacity(element, rootElement, style),
        },
    };
};

const getPseudoElementFrameNode = (
    element: HTMLElement,
    pseudoElement: '::after' | '::before',
    rootRect: DOMRect,
    rootElement: HTMLElement
): FigmaExportNode | null => {
    const style = window.getComputedStyle(element, pseudoElement);
    const rect = getPseudoElementRect(element, style, rootRect);

    if (
        style.content === 'none' ||
        getIconUrl(style) ||
        rect.width <= 0 ||
        rect.height <= 0 ||
        !hasVisibleBoxStyle(style)
    ) {
        return null;
    }

    return {
        type: 'frame',
        name: `${pseudoElement.replace('::', '')} ${element.className || element.tagName.toLowerCase()}`,
        x: rect.x,
        y: rect.y,
        width: rect.width,
        height: rect.height,
        style: getElementStyle(style, undefined, getEffectiveOpacity(element, rootElement, style)),
        children: [],
    };
};

const getBackgroundImageSvgNode = async (
    element: HTMLElement,
    rootRect: DOMRect,
    rootElement: HTMLElement
): Promise<FigmaExportNode | null> => {
    const style = window.getComputedStyle(element);
    const backgroundImageUrl = getBackgroundImageUrl(style);
    const rect = element.getBoundingClientRect();

    if (!backgroundImageUrl || rect.width <= 0 || rect.height <= 0) {
        return null;
    }

    const svg = await fetchIconSvg(backgroundImageUrl);

    if (!svg) {
        return null;
    }

    const relativeRect = getRelativeRect(rect, rootRect);

    return {
        type: 'svg',
        name: `background ${backgroundImageUrl.split('/').pop()?.replace('.svg', '') ?? 'image'}`,
        sourceMode: 'image',
        sourceUrl: backgroundImageUrl,
        svg,
        x: relativeRect.x,
        y: relativeRect.y,
        width: relativeRect.width,
        height: relativeRect.height,
        style: {
            opacity: getEffectiveOpacity(element, rootElement),
        },
    };
};

const getInputTextNode = (
    element: HTMLElement,
    rootRect: DOMRect,
    rootElement: HTMLElement
): FigmaExportNode | null => {
    if (
        !(element instanceof HTMLInputElement) ||
        ['button', 'checkbox', 'hidden', 'radio', 'range', 'submit'].includes(element.type)
    ) {
        return null;
    }

    const text = normalizeName(element.value || element.placeholder || '');

    if (!text) {
        return null;
    }

    const rect = element.getBoundingClientRect();
    const style = window.getComputedStyle(element);
    const isPlaceholder = !element.value && Boolean(element.placeholder);
    const textStyle = isPlaceholder ? window.getComputedStyle(element, '::placeholder') : style;
    const lineHeight = getLineHeight(style, element);
    const paddingLeft = getNumberValue(style.paddingLeft);
    const paddingRight = getNumberValue(style.paddingRight);

    return {
        type: 'text',
        name: `${isPlaceholder ? 'placeholder' : 'input'} ${text.slice(0, 40)}`,
        text,
        textAutoResize: 'fixed',
        x: rect.left - rootRect.left + paddingLeft,
        y: rect.top - rootRect.top + (rect.height - lineHeight) / 2,
        width: Math.max(1, rect.width - paddingLeft - paddingRight),
        height: lineHeight,
        style: {
            color: parseColor(textStyle.color) ?? parseColor(style.color) ?? transparentColor,
            fontFamily: getFontFamily(style.fontFamily),
            fontSize: getNumberValue(style.fontSize),
            fontWeight: Number.parseInt(style.fontWeight, 10) || 400,
            lineHeight,
            textAlign: style.textAlign,
            textTransform: style.textTransform,
            opacity: getEffectiveOpacity(element, rootElement, isPlaceholder ? textStyle : undefined),
        },
    };
};

const getTextNode = (textNode: Text, rootRect: DOMRect, rootElement: HTMLElement): FigmaExportNode | null => {
    const text = normalizeName(textNode.textContent ?? '');
    const parentElement = textNode.parentElement;

    if (!text || !parentElement) {
        return null;
    }

    const range = document.createRange();
    range.selectNodeContents(textNode);

    const rect = range.getBoundingClientRect();
    const style = window.getComputedStyle(parentElement);
    const textAutoResize = isSingleLineText(range, style) ? 'widthAndHeight' : 'fixed';
    const lineHeight = getLineHeight(style, parentElement);
    range.detach();

    if (rect.width <= 0 || rect.height <= 0) {
        return null;
    }

    const relativeRect = getRelativeRect(rect, rootRect);
    const parentRect = parentElement.getBoundingClientRect();
    const shouldUseParentLineBox =
        textAutoResize === 'widthAndHeight' &&
        parentRect.height > rect.height + 0.5 &&
        (isFlexDisplay(style.display) || hasFlexAncestor(parentElement, rootElement) || lineHeight > rect.height + 0.5);
    const y = shouldUseParentLineBox
        ? parentRect.top - rootRect.top + Math.max(0, (parentRect.height - lineHeight) / 2)
        : relativeRect.y;
    const height = shouldUseParentLineBox ? Math.max(lineHeight, relativeRect.height) : relativeRect.height;

    return {
        type: 'text',
        name: `text ${text.slice(0, 40)}`,
        text,
        textAutoResize,
        x: relativeRect.x,
        y,
        width: relativeRect.width,
        height,
        style: {
            color: parseColor(style.color) ?? transparentColor,
            fontFamily: getFontFamily(style.fontFamily),
            fontSize: getNumberValue(style.fontSize),
            fontWeight: Number.parseInt(style.fontWeight, 10) || 400,
            lineHeight,
            textAlign: style.textAlign,
            textTransform: style.textTransform,
            opacity: getEffectiveOpacity(parentElement, rootElement),
        },
    };
};

const getChildNodes = async (
    element: HTMLElement,
    rootRect: DOMRect,
    rootElement: HTMLElement
): Promise<FigmaExportNode[]> => {
    const childNodes: FigmaExportNode[] = [];
    const foregroundChildNodes: Array<{ node: FigmaExportNode; zIndex: number }> = [];
    const backgroundImageNode = await getBackgroundImageSvgNode(element, rootRect, rootElement);
    const beforeSvgNode = await getPseudoElementSvgNode(element, '::before', rootRect, rootElement);
    const beforeTriangleNode = getPseudoElementBorderTriangleSvgNode(element, '::before', rootRect, rootElement);
    const beforeFrameNode = beforeTriangleNode
        ? null
        : getPseudoElementFrameNode(element, '::before', rootRect, rootElement);
    const inputTextNode = getInputTextNode(element, rootRect, rootElement);

    if (backgroundImageNode) {
        childNodes.push(backgroundImageNode);
    }

    if (beforeSvgNode) {
        childNodes.push(beforeSvgNode);
    }

    if (beforeTriangleNode) {
        childNodes.push(beforeTriangleNode);
    }

    if (beforeFrameNode) {
        childNodes.push(beforeFrameNode);
    }

    if (inputTextNode) {
        childNodes.push(inputTextNode);
    }

    for (const child of element.childNodes) {
        if (child instanceof HTMLElement) {
            const elementNode = await getElementNode(child, rootRect, rootElement);

            if (elementNode) {
                const zIndex = getPositiveZIndex(child);

                if (zIndex > 0) {
                    foregroundChildNodes.push({ node: { ...elementNode, paintOrder: 'foreground' }, zIndex });
                } else {
                    childNodes.push(elementNode);
                }
            }
            continue;
        }

        if (child instanceof Text) {
            const textNode = getTextNode(child, rootRect, rootElement);

            if (textNode) {
                childNodes.push(textNode);
            }
        }
    }

    foregroundChildNodes
        .sort((firstChild, secondChild) => firstChild.zIndex - secondChild.zIndex)
        .forEach(({ node }) => {
            childNodes.push(node);
        });

    const afterSvgNode = await getPseudoElementSvgNode(element, '::after', rootRect, rootElement);
    const afterTriangleNode = getPseudoElementBorderTriangleSvgNode(element, '::after', rootRect, rootElement);
    const afterFrameNode = afterTriangleNode
        ? null
        : getPseudoElementFrameNode(element, '::after', rootRect, rootElement);

    if (afterSvgNode) {
        childNodes.push(afterSvgNode);
    }

    if (afterTriangleNode) {
        childNodes.push(afterTriangleNode);
    }

    if (afterFrameNode) {
        childNodes.push(afterFrameNode);
    }

    return childNodes;
};

const getElementNode = async (
    element: HTMLElement,
    rootRect: DOMRect,
    rootElement: HTMLElement,
    options: ElementNodeOptions = {}
): Promise<FigmaExportNode | null> => {
    if (shouldIgnoreExportElement(element)) {
        return null;
    }

    const rect = element.getBoundingClientRect();

    if (rect.width <= 0 || rect.height <= 0) {
        return null;
    }

    const style = window.getComputedStyle(element);
    const relativeRect = getRelativeRect(rect, rootRect);
    const fallbackBackgroundColor = options.useEffectiveBackgroundColor
        ? getEffectiveBackgroundColor(element)
        : undefined;

    return {
        type: 'frame',
        name: getNodeName(element),
        x: relativeRect.x,
        y: relativeRect.y,
        width: relativeRect.width,
        height: relativeRect.height,
        style: getElementStyle(
            style,
            fallbackBackgroundColor,
            getEffectiveOpacity(element, rootElement, undefined, element === rootElement)
        ),
        children: await getChildNodes(element, rootRect, rootElement),
    };
};

export const getFigmaPayloadFromElement = async (element: HTMLElement) => {
    const rootRect = element.getBoundingClientRect();
    const root = await getElementNode(element, rootRect, element, { useEffectiveBackgroundColor: true });

    if (!root) {
        return '';
    }

    const payload: FigmaExportPayload = {
        generator: 'uikit-demo',
        version: 1,
        root,
    };

    return JSON.stringify(payload);
};
